import React, { useState, useEffect } from 'react';
import { attributeSensitivityService } from '../../services';
import { DataTable } from '../../components';
import { useSideDialog, useModalDialog, useGlobalState, useMessage } from '../../hooks';
import { handleError, AttributeSensitivity, Role } from '../../helpers';

export const AttributeSensitivities = () => {
  const [attributeSensitivities, setAttributeSensitivities] = useState([]);
  const [loggedInUser, setLoggedInUser] = useGlobalState('loggedInUser');
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const clearSelectedItems = () => {
    setSelectedItems([]);
  };

  const { showMessage } = useMessage();
  const { showSideDialog, hideSideDialog, setSideDialog } = useSideDialog(clearSelectedItems);
  const { showModalDialog, hideModalDialog } = useModalDialog();

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    attributeSensitivityService
      .getAll()
      .then(res => {
        setAttributeSensitivities(
          res.attribute_sensitivities.map(x => ({
            ...x,
            attribute_sensitivity_appearance: x.attribute_sensitivity_name,
          }))
        );
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
        showMessage(err, 'error');
      });
  };

  const addAttributeSensitivity = async data => {
    const props = { attribute_sensitivities: [data] };

    await attributeSensitivityService
      .create(props)
      .then(res => {
        setSideDialog(prev => ({ ...prev, formErrors: {} }));
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const editAttributeSensitivity = async data => {
    const props = { attribute_sensitivities: Array.isArray(data) ? data : [data] };

    await attributeSensitivityService
      .update(props)
      .then(async res => {
        fetchItems();
        showMessage(res.message);
        clearSelectedItems();
        hideSideDialog();
      })
      .catch(err => {
        handleError({ err, showMessage, setSideDialog });
        throw err;
      });
  };

  const deleteAttributeSensitivity = async data => {
    await attributeSensitivityService
      .destroy({ attribute_sensitivities: data })
      .then(async res => {
        hideModalDialog();
        clearSelectedItems();
        fetchItems();
        showMessage(res.message);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const editable = loggedInUser && loggedInUser.user_role_name === Role.admin;

  const booleanValues = {
    true: { label: 'Yes', className: 'pill pill-black' },
    false: { label: 'No', className: 'pill pill-grey' },
  };

  return (
    <DataTable
      columns={[
        {
          id: 'attribute_sensitivity_name',
          name: 'Sensitivity Class',
          tooltip: 'attribute_sensitivity_description',
          className: 'bold',
        },
        {
          id: 'attribute_sensitivity_appearance',
          name: 'Appearance',
          type: 'pill',
          value: 'attribute_sensitivity_level',
          values: AttributeSensitivity,
          tooltip: 'attribute_sensitivity_name',
        },
        { id: 'attribute_sensitivity_description', name: 'Description', viewMode: 'descriptions' },
        { id: 'is_default', name: 'Is Default', type: 'boolean', values: booleanValues },
        {
          id: 'is_allowed_for_pii',
          name: 'Allowed For PII',
          type: 'boolean',
          values: booleanValues,
        },
      ]}
      buttons={[
        {
          label: '+ Add',
          action: 'add',
          mainButton: true,
          onClick: items =>
            showSideDialog(
              'addAttributeSensitivity',
              items || selectedItems,
              addAttributeSensitivity
            ),
        },
        {
          label: 'Edit',
          action: 'edit',
          onClick: items =>
            showSideDialog(
              'editAttributeSensitivity',
              items || selectedItems,
              editAttributeSensitivity
            ),
        },
        {
          label: 'Delete',
          action: 'delete',
          onClick: items =>
            showModalDialog(
              'deleteAttributeSensitivity',
              items || selectedItems,
              deleteAttributeSensitivity
            ),
        },
      ]}
      data={attributeSensitivities}
      idColumn="attribute_sensitivity_id"
      selectedItems={selectedItems}
      setSelectedItems={setSelectedItems}
      loading={loading}
      filterable={true}
      filterObjects={['attribute_sensitivity_name', 'attribute_sensitivity_description']}
      showViewModes={true}
    />
  );
};
